import React, { useEffect, useState, useMemo } from "react";
import { GetServiceCaller } from "../../../../services/serviceCaller";
import { API_URL } from "../../../../constants";
import { Table, Form } from "react-bootstrap";
import moment from "moment";
import Pagination from "../../../../components/Pagination";
import SearchBar from "../../../../components/SearchBar";
import ExportToExcel from "../../../../components/ExportToExcel";
import { Accordion, Card, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faMinus } from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import { updateNavbarHeading } from "../../../../constants/layout/store/filterSlice";
import { FaSpinner } from "react-icons/fa";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import CustomDatePicker from "../../../../components/Custom-Datepicker";

const AllReport = (data) => {
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(updateNavbarHeading("All Reports"));
  }, []);

  const [resource, setResource] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [selectedRecordsPerPage, setSelectedRecordsPerPage] = useState(10);
  const [activeClientKeys, setActiveClientKeys] = useState([]);
  const [activeProjectKeys, setActiveProjectKeys] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);

  const [showDropdown, setShowDropdown] = useState(false);

  // Column visibility configuration
  const [columnVisibility, setColumnVisibility] = useState({
    InvoiceType: true,
    InvoiceNo: true,
    InvoiceDate: true,
    EmployeeName: true,
    ProformaAmt: true,
    TaxPercent: true,
    ExchangeRate: true,
    InvoicedAmount: true,
    PaymentTermDays: true,
    PaymentDueDate: true,
    FollowUpDate: true,
    PaymentReceivedDate: true,
    OverDueDays: true,
    Remarks: true,
    BasicAmount: true,
    InvoicedDays: true,
  });

  // Columns configuration with proper date formatting
  const columns = [
    { id: "InvoiceType", label: "Invoice Type", accessor: "InvoiceType" },
    { id: "InvoiceNo", label: "Invoice", accessor: "InvoiceNo" },
    {
      id: "InvoiceDate",
      label: "Invoice Date",
      accessor: "InvoiceDate",
      format: (val) => formatDate(val),
    },
    { id: "EmployeeName", label: "Employee", accessor: "EmployeeName" },
    { id: "ProformaAmt", label: "Proforma Amt", accessor: "ProformaAmt" },
    { id: "TaxPercent", label: "Tax %", accessor: "TaxPercent" },
    { id: "ExchangeRate", label: "Exchange Rate", accessor: "ExchangeRate" },
    { id: "InvoicedAmount", label: "Invoice Amt", accessor: "InvoicedAmount" },
    {
      id: "PaymentTermDays",
      label: "Pymt Term Day",
      accessor: "PaymentTermDays",
    },
    {
      id: "PaymentDueDate",
      label: "Pymt.Due Date",
      accessor: "PaymentDueDate",
      format: (val) => formatDate(val),
    },
    { id: "BasicAmount", label: "Basic Amt", accessor: "BasicAmount" },
    { id: "InvoicedDays", label: "Inv. Days", accessor: "InvoicedDays" },
    {
      id: "FollowUpDate",
      label: "Follow-up Date",
      accessor: "FollowUpDate",
      format: (val) => formatDate(val),
    },
    {
      id: "PaymentReceivedDate",
      label: "Pymt.Recd Date",
      accessor: "PaymentReceivedDate",
      format: (val) => formatDate(val),
    },
    { id: "OverDueDays", label: "OverDue", accessor: "OverDueDays" },
    { id: "Remarks", label: "Remarks", accessor: "Remarks" },
  ];

  // Utility function to format dates
  const formatDate = (dateValue) => {
    if (!dateValue) return "";
    try {
      return moment(dateValue).isValid()
        ? moment(dateValue).format("DD-MMM-YYYY")
        : "";
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const toggleColumnVisibility = (columnId) => {
    setColumnVisibility((prev) => ({
      ...prev,
      [columnId]: !prev[columnId],
    }));
  };

  const [formData, setFormData] = useState({
    fromDate: "",
    toDate: "",
    Overdue: 0,
  });

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const response = await GetServiceCaller(
        `${API_URL}projectmanagement/reports/8?P_FromDate&P_ToDate&P_OverDueDays`
      );

      if (response?.status === 200) {
        // Process dates in the response data
        const processedData = processDateFields(response?.data?.data || []);
        setResource(processedData);
      }
    } catch (error) {
      console.error("Error fetching report:", error);
    } finally {
      setLoading(false);
    }
  };

  // Process date fields to ensure they're in correct format
  const processDateFields = (data) => {
    return data.map(item => {
      const dateFields = [
        'InvoiceDate',
        'PaymentDueDate',
        'FollowUpDate',
        'PaymentReceivedDate'
      ];

      const processedItem = { ...item };

      dateFields.forEach(field => {
        if (item[field]) {
          // Convert to Date object if it's a string
          processedItem[field] = typeof item[field] === 'string'
            ? new Date(item[field])
            : item[field];
        }
      });

      return processedItem;
    });
  };

  // Filter data based on search term
  const filteredData = useMemo(() => {
    return resource.filter((item) =>
      Object.values(item).some((value) =>
        String(value).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  }, [resource, searchTerm]);

  // Pagination logic
  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Get current items for pagination
  const getCurrentItems = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
    return filteredData.slice(startIndex, endIndex);
  };

  const currentItems = getCurrentItems();

  // Create nested structure for display
  const nestedData = useMemo(() => {
    return currentItems.reduce((acc, item) => {
      if (!acc[item.ClientName]) {
        acc[item.ClientName] = {};
      }
      if (!acc[item.ClientName][item.ProjectName]) {
        acc[item.ClientName][item.ProjectName] = [];
      }
      acc[item.ClientName][item.ProjectName].push(item);
      return acc;
    }, {});
  }, [currentItems]);
  useEffect(() => {
    if (!loading && Object.keys(nestedData).length > 0 && !isInitialized) {
      const allClientKeys = Object.keys(nestedData).map((_, i) => i.toString());
      setActiveClientKeys(allClientKeys);

      const allProjectKeys = {};
      Object.entries(nestedData).forEach(([_, projects], clientIndex) => {
        allProjectKeys[clientIndex.toString()] = Object.keys(projects).map((_, i) => i.toString());
      });

      setActiveProjectKeys(allProjectKeys);
      setIsInitialized(true);
    }
  }, [nestedData, loading, isInitialized]);

  // Accordion handlers
  const handleClientToggle = (clientKey) => {
    setActiveClientKeys((prev) =>
      prev.includes(clientKey)
        ? prev.filter((key) => key !== clientKey)
        : [...prev, clientKey]
    );
  };


  const handleProjectToggle = (clientKey, projectKey) => {
    setActiveProjectKeys((prev) => {
      const openProjects = prev[clientKey] || [];
      const isOpen = openProjects.includes(projectKey);

      return {
        ...prev,
        [clientKey]: isOpen
          ? openProjects.filter((k) => k !== projectKey)
          : [...openProjects, projectKey],
      };
    });
  };


  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleRecordsPerPage = (e) => {
    const newRecordsPerPage = Number(e.target.value);
    const currentStartIndex = (currentPage - 1) * selectedRecordsPerPage;
    const newPage = Math.floor(currentStartIndex / newRecordsPerPage) + 1;

    setSelectedRecordsPerPage(newRecordsPerPage);
    setCurrentPage(newPage);
    setItemsPerPage(newRecordsPerPage);
  };

  const recordsPerPageOption = [
    { value: 10, label: "10" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 100, label: "100" },
    { value: filteredData.length, label: "All" },
  ];

  // Form handlers
  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    const newValue = type === "checkbox" ? (checked ? 1 : 0) : value;

    setFormData((prevState) => ({
      ...prevState,
      [name]: newValue,
    }));
  };

  const handleGetData = async () => {
    const { fromDate, toDate, Overdue } = formData;
    setLoading(true);

    const formattedFromDate = fromDate
      ? moment(fromDate).format("YYYY-MM-DD")
      : "";
    const formattedToDate = toDate ? moment(toDate).format("YYYY-MM-DD") : "";

    const url = `${API_URL}projectmanagement/project-training-invoice-report?P_FromDate=${formattedFromDate}&P_ToDate=${formattedToDate}&P_OverDueDays=${Overdue || ""
      }`;

    try {
      const response = await GetServiceCaller(url);
      if (response?.status === 200) {
        const processedData = processDateFields(response?.data?.data || []);
        setResource(processedData);
      }
    } catch (err) {
      console.error("API error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Styles
  const customIndicator = {
    display: "inline-block",
    width: "20px",
    height: "20px",
    borderRadius: "50%",
    marginBottom: "0px",
    verticalAlign: "middle",
    marginLeft: "10px",
    marginRight: "3px",
    boxShadow:
      "0 2px 9px 0 rgba(0, 0, 0, 0.18), 0 2px 7px 0 rgba(0, 0, 0, 0.15)",
    border: "solid 2px #fff",
  };

  // Default column style
  const columnStyle = {
    minWidth: "90px",
    textAlign: "center",
    verticalAlign: "middle"
  };

  // Special column styles
  const specialColumnStyles = {
    EmployeeName: { minWidth: "120px" },
    FollowUpDate: { minWidth: "100px" },
    PaymentReceivedDate: { minWidth: "100px" },
    PaymentDueDate: { minWidth: "100px" },
    InvoiceDate: { minWidth: "100px" },
    Remarks: { minWidth: "150px" },
    TaxPercent: { minWidth: "80px" },
    OverDueDays: { minWidth: "80px" }
  };

  // Excel export function
  const InvoiceExportData = (data, columnVisibility, columns) => {
    const nestedData = data.reduce((acc, item) => {
      if (!acc[item.ClientName]) acc[item.ClientName] = {};
      if (!acc[item.ClientName][item.ProjectName]) {
        acc[item.ClientName][item.ProjectName] = [];
      }
      acc[item.ClientName][item.ProjectName].push(item);
      return acc;
    }, {});

    const visibleColumns = columns.filter(col => columnVisibility[col.id]);
    const exportData = [];

    Object.entries(nestedData).forEach(([clientName, projects]) => {
      const clientRow = {};
      visibleColumns.forEach((col, index) => {
        clientRow[col.label] = index === 0 ? `Client: ${clientName}` : '';
      });
      exportData.push(clientRow);

      Object.entries(projects).forEach(([projectName, items]) => {
        const projectRow = {};
        visibleColumns.forEach((col, index) => {
          projectRow[col.label] = index === 0
            ? `Project: ${projectName} (SOW/PO/SO ${items[0]?.ProjectInvoiceDetailNumber || 'N/A'})`
            : '';
        });
        exportData.push(projectRow);

        items.forEach(item => {
          const row = {};
          visibleColumns.forEach(col => {
            if (col.format) {
              row[col.label] = col.format(item[col.accessor]);
            } else {
              row[col.label] = item[col.accessor] || '';
            }
          });
          exportData.push(row);
        });
      });
    });

    return exportData;
  };

  return (
    <>
      <div className="container-fluid" id="skill_section">
        <div className="container-fluid" id="skill-table-area">
          <div className="table-responsive">
            <div className="d-flex justify-content-between">
              <div className="d-flex">
                <div className="me-1">
                  <SearchBar
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                  />
                </div>
                <OverlayTrigger
                  placement="right"
                  overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
                >
                  <div className="btn-group me-2">
                    <button
                      type="button"
                      className="dropdown-toggle skill-button"
                      data-bs-toggle="dropdown"
                      onClick={() => setShowDropdown(!showDropdown)}
                      aria-expanded="false"
                      id="excel-btn"
                    >
                      <i className="bi bi-funnel-fill"></i>
                    </button>
                    <ul
                      className={`dropdown-menu p-2 project-status-filter ${showDropdown ? "show" : ""
                        }`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="row">
                        <div className="col-md-4">
                          <label className="form-label">From Date</label>
                          <CustomDatePicker
                            name="fromDate"
                            className="form-control"
                            placeholderText="DD-MMM-YYYY"
                            handleChange={(e) => handleChange(e)}
                            value={formData.fromDate}
                          />
                        </div>
                        <div className="col-md-4">
                          <label className="form-label">To Date</label>
                          <CustomDatePicker
                            name="toDate"
                            className="form-control"
                            placeholderText="DD-MMM-YYYY"
                            handleChange={(e) => handleChange(e)}
                            value={formData.toDate}
                          />
                        </div>
                        <div className="col-sm-3 mt-4">
                          <label
                            htmlFor="flexSwitchCheckChecked"
                            className="form-label me-2"
                          >
                            Overdue
                          </label>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            name="Overdue"
                            id="flexSwitchCheckChecked"
                            checked={formData.Overdue === 1}
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="row mt-2">
                        <div className="text-end">
                          <button
                            className="custom-btn mx-2"
                            onClick={() => {
                              setFormData({
                                fromDate: "",
                                toDate: "",
                                Overdue: 0,
                              });
                              setShowDropdown(false);
                              fetchReport();
                            }}
                          >
                            Reset
                          </button>
                          <button
                            className="custom-btn"
                            onClick={() => {
                              handleGetData();
                              setShowDropdown(false);
                            }}
                          >
                            Get Data
                          </button>
                        </div>
                      </div>
                    </ul>
                  </div>
                </OverlayTrigger>
              </div>
              <div className="d-flex justify-content-end">
                <div className="me-1">
                  <ExportToExcel
                    data={InvoiceExportData(
                      resource,
                      columnVisibility,
                      columns
                    )}
                    filename="All Report.csv"
                  />
                </div>
                <div className="d-flex justify-content-end">
                  <div className="me-1">
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip id="tooltip-top">Column Chooser</Tooltip>
                      }
                    >
                      <div className="btn-group me-2">
                        <button
                          type="button"
                          className="dropdown-toggle skill-button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                          id="excel-btn"
                        >
                          <i className="bi bi-list"></i>
                        </button>
                        <ul className="dropdown-menu p-2">
                          <div className="row">
                            <Form.Group>
                              {columns.map((column) => (
                                <Form.Check
                                  key={column.id}
                                  type="checkbox"
                                  id={`checkbox-${column.id}`}
                                  label={column.label}
                                  checked={columnVisibility[column.id]}
                                  onChange={() =>
                                    toggleColumnVisibility(column.id)
                                  }
                                />
                              ))}
                            </Form.Group>
                          </div>
                        </ul>
                      </div>
                    </OverlayTrigger>
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-1 d-flex align-items-center">
              <p style={{ ...customIndicator, background: "#bad893" }}></p>
              <span className="me-3">FixedBid</span>
              <p style={{ ...customIndicator, background: "#ffe4a4" }}></p>
              <span>Consolidated</span>
            </div>
            <Table bordered hover responsive>
              <thead id="thcolor" className="text-center">
                <tr>
                  <th style={{ ...columnStyle, width: "50px" }}>##</th>
                  {columns.map((column) => (
                    columnVisibility[column.id] && (
                      <th
                        key={column.id}
                        style={{
                          ...columnStyle,
                          ...(specialColumnStyles[column.id] || {})
                        }}
                      >
                        {column.label}
                      </th>
                    )
                  ))}
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={Object.values(columnVisibility).filter(Boolean).length + 1} className="text-center py-4">
                      <div className="d-flex justify-content-center">
                        <FaSpinner className="spinner" />
                      </div>
                      <div className="text-muted mt-2">Loading</div>
                    </td>
                  </tr>
                ) : Object.keys(nestedData).length === 0 ? (
                  <tr>
                    <td colSpan={Object.values(columnVisibility).filter(Boolean).length + 1} className="text-center text-muted py-3">
                      No Record Found
                    </td>
                  </tr>
                ) : (
                  Object.entries(nestedData).map(([clientName, projects], clientIndex) => {
                    const clientKey = clientIndex.toString();
                    return (
                      <tr key={clientKey}>
                        <td colSpan={Object.values(columnVisibility).filter(Boolean).length + 1} className="p-0">
                          <Accordion activeKey={activeClientKeys}>
                            <Accordion.Item eventKey={clientKey}>
                              <Accordion.Header className="AccordioninvoiceClient_header" onClick={() => handleClientToggle(clientKey)}>
                                <span className="me-2 accordion_icon">
                                  <FontAwesomeIcon icon={activeClientKeys.includes(clientKey) ? faMinus : faPlus} />
                                </span>
                                <div className="flex-grow-1 text-dark fw-bold">
                                  {clientName}
                                </div>

                              </Accordion.Header>

                              <Accordion.Body className="p-0">
                                <Accordion activeKey={activeProjectKeys[clientKey] || []}>
                                  {Object.entries(projects).map(([projectName, items], projectIndex) => {
                                    const projectKey = projectIndex.toString();
                                    return (
                                      <Accordion.Item eventKey={projectKey} key={projectKey}>
                                        <Accordion.Header onClick={() => handleProjectToggle(clientKey, projectKey)}>
                                          <div className="flex-grow-1 px-3 text-dark">
                                            {projectName}
                                            <span className="text-muted">
                                              {" "}(SOW/PO/SO {items[0]?.ProjectInvoiceDetailNumber || "N/A"})
                                            </span>
                                          </div>
                                          <span className="me-2 accordion_icon">
                                            <FontAwesomeIcon icon={
                                              (activeProjectKeys[clientKey] || []).includes(projectKey)
                                                ? faMinus
                                                : faPlus
                                            } />
                                          </span>
                                        </Accordion.Header>

                                        <Accordion.Body className="p-0">
                                          <Table bordered striped hover responsive>
                                            <tbody>
                                              {items.map((data, subIndex) => (
                                                <tr key={subIndex}>
                                                  <td style={{ ...columnStyle, width: "50px" }}>{subIndex + 1}</td>
                                                  {columns.map((column) => (
                                                    columnVisibility[column.id] && (
                                                      <td
                                                        key={column.id}
                                                        style={{
                                                          ...columnStyle,
                                                          ...(specialColumnStyles[column.id] || {})
                                                        }}
                                                      >
                                                        {column.format
                                                          ? column.format(data[column.accessor])
                                                          : data[column.accessor] || ""}
                                                      </td>
                                                    )
                                                  ))}
                                                </tr>
                                              ))}
                                            </tbody>
                                          </Table>
                                        </Accordion.Body>
                                      </Accordion.Item>
                                    );
                                  })}
                                </Accordion>
                              </Accordion.Body>
                            </Accordion.Item>
                          </Accordion>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>

            <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-sm-between mb-3">
              <div className="d-flex justify-content-between px-2">

                <span className="d-flex align-items-center">
                  Showing {(currentPage - 1) * itemsPerPage + 1} -{" "}
                  {Math.min(currentPage * itemsPerPage, filteredData.length)} of{" "}
                  {filteredData.length} <span className="ms-1">results</span>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      const newItemsPerPage = Number(e.target.value);
                      setItemsPerPage(newItemsPerPage);
                      setCurrentPage(1);
                    }}
                    className="mx-2 form-select-sm"
                  >
                    {recordsPerPageOption.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </span>
              </div>
          <div className="d-flex justify-content-center justify-content-sm-end mt-2 mt-sm-0 w-100">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={totalItems}
                  currentPage={currentPage}
                  onPageChange={handlePageChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AllReport;